<script setup lang="ts">
import {
  type ContractFileWithRelations,
  type RelatedContractFilesResponse,
  type ContractManagementContractSearchResult,
  type ContractManagementContractInDb,
  type FilterableSearchQuery,
} from '@/api';
import {
  getEntityDetailConfig,
  type EntityDetailConfig,
  type ContractManagementEntityInDb,
  type ContractManagementEntityType,
} from '@/types/contractManagement/detailConfig';
import { getEntityEditFields, type EditFieldConfig } from '@/types/contractManagement/editConfig';
import { IconMore } from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaFileLinkCard from '@/components/design/domain/contractManagement/GaiaFileLinkCard.vue';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import {
  contractIconStates,
  contractStateLabel,
  type ContractIconState,
} from '@/components/design/domain/contractManagement/contractStateIcons';
import GaiaFilterBar from '@/components/design/composite/GaiaFilterBar.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import { gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import {
  allFieldsOf,
  entityConfigs,
  isFieldShown,
  toText,
  type EntityField,
  type EntityRow,
} from './contractManagement/entityListConfig';
import EntityInlineField from './contractManagement/EntityInlineField.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import AssignContractFileModal from '@/components/contractManagement/AssignContractFileModal.vue';
import AssignContractModal from '@/components/contractManagement/AssignContractModal.vue';
import ContractManagementEditDialog from '@/components/contractManagement/ContractManagementEditDialog.vue';
import ContractualPartyShares from '@/components/contractManagement/ContractualPartyShares.vue';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { askAiLimitWarning, askAiRoute } from '@/utils/askAi';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { logger } from '@/utils/logger';
import {
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
  gateOption,
} from '@/utils/accessPermission';
import { isAccessDenied } from '@/utils/errorHandler';
import { safeStringify } from '@/utils/jsonHelpers';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { contractRoute, contractFileRoute } from '@/router/links';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();

// Get entity type and uid from route
const entityType = computed(() => route.params.entityType as string);
const entityUid = computed(() => route.params.uid as string);

// Get configuration for this entity type
const config = computed<EntityDetailConfig | undefined>(() => {
  return getEntityDetailConfig(entityType.value);
});

// State
const loading = ref<boolean>(false);

const entity = ref<ContractManagementEntityInDb | null>(null);
/** The backend refused the record: deleted, no access, or another organisation's. */
const unavailable = ref(false);
/** The record fetch failed — not merely "hasn't answered yet", which is no reason for an error. */
const entityFailed = ref(false);
const contractFiles = ref<ContractFileWithRelations[]>([]);
const contracts = ref<ContractManagementContractInDb[]>([]);
const totalCount = ref<number>(0);
const selectedContractFile = ref<ContractFileWithRelations | null>(null);
const selectedPage = ref<number | null>(null);
const pdfKey = ref(0);

type RecordTab = 'detail' | 'contract-files' | 'contracts' | 'sharing';

const activeTab = ref<RecordTab>('detail');
const showDocumentPreview = ref<boolean>(false);
const showEditDialog = ref<boolean>(false);
const saving = ref<boolean>(false);
const deleting = ref<boolean>(false);
const removingContractUids = ref<Set<string>>(new Set());
/** Contract whose inline "Remove from this matter?" strip is open. */
const confirmRemoveContractUid = ref<string | null>(null);
const showAssignModal = ref<boolean>(false);
const showAssignContractModal = ref<boolean>(false);

const showsContracts = computed(() => config.value?.showsContracts === true);
/** Contractual parties are shared like contracts; no other record is. */
const isParty = computed(() => entityType.value === 'contractual-party');

/**
 * The caller's bits on a party; other records carry none, which gates nothing.
 * Editing (and assigning a file, which links the party) needs WRITE; deleting
 * and sharing need ADMIN — the backend's split.
 */
const partyBits = computed(() =>
  isParty.value && entity.value && 'permissions' in entity.value
    ? entity.value.permissions
    : undefined
);
const writeBlock = computed(() => deniedReason(partyBits.value, ACCESS_PERMISSION_WRITE));
const adminBlock = computed(() => deniedReason(partyBits.value, ACCESS_PERMISSION_ADMIN));
const shareCount = ref<number | undefined>(undefined);

/**
 * The Sharing badge, loaded with the page like the files count. The tab's own
 * list re-emits it once opened and after every share change.
 */
async function fetchShareCount(): Promise<void> {
  shareCount.value = undefined;
  if (!isParty.value) return;
  try {
    const [users, roles] = await Promise.all([
      ContractualPartyShareService.getUserSharesByParty(entityUid.value),
      ContractualPartyShareService.getRoleSharesByParty(entityUid.value),
    ]);
    shareCount.value = users.length + roles.length;
  } catch (error) {
    // The badge is a nicety; the tab reports its own load failure.
    logger.error('Error fetching share count:', error);
  }
}

// A record either files contract files or contracts, never both, so the second
// tab swaps rather than the row growing.
const recordTabs = computed<GaiaTabBarItem[]>(() => [
  { key: 'detail', label: 'Detail' },
  showsContracts.value
    ? { key: 'contracts', label: 'Contracts', count: totalCount.value }
    : { key: 'contract-files', label: 'Contract files', count: totalCount.value },
  ...(isParty.value ? [{ key: 'sharing', label: 'Sharing', count: shareCount.value }] : []),
]);

const searchText = ref<string>('');
const latestRevisionOnly = ref<boolean>(true);
const currentPage = ref<number>(1);
const pageSize = ref<number>(10); // Default page size, matching list views
const sortKey = ref<string>('created_at');
const sortDirection = ref<'asc' | 'desc'>('desc');
const { columnFilters, setColumnFilters } = useListQuerySync();

const activeFilters = ref<Record<string, unknown>>({ ...columnFilters.value });

watch(
  activeFilters,
  () => {
    setColumnFilters(activeFilters.value);
  },
  { deep: true }
);
const filterOptions = ref<Record<string, (string | null)[]>>({});

// PDF preview ref
const pdfPreviewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();

// Computed properties
const selectedVaultFileUid = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  return selectedContractFile.value?.vault_file?.uid || null;
});

const selectedVaultFileName = computed(() => {
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    selectedContractFile.value?.revision?.file_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    selectedContractFile.value?.vault_file?.current_revision?.file_name ||
    'Document'
  );
});

const selectedRevisionUid = computed(() => selectedContractFile.value?.revision?.uid ?? undefined);

const selectedRevisionPreviewStatus = computed(
  () => selectedContractFile.value?.revision?.preview_status ?? null
);

const displayName = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return config.value?.displayName || 'Entity';
});

const entityDisplayName = computed(() => {
  if (!entity.value) return '';
  // Try common name fields
  const entityRecord = entity.value as Record<string, unknown>;
  return (
    (entityRecord.name as string) ||
    (entityRecord.title as string) ||
    (entityRecord.text as string) ||
    ''
  );
});

/**
 * Format a field value for display based on its type and configuration
 */
function formatFieldValue(fieldConfig: EditFieldConfig, rawValue: unknown): string {
  // Handle null/undefined/empty values
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return '—';
  }

  // Tags are a string[] — read them out as a list, not as the JSON array
  // safeStringify would produce.
  if (fieldConfig.type === 'tags') {
    if (!Array.isArray(rawValue)) return '—';
    const tags = rawValue.map(tag => safeStringify(tag).trim()).filter(Boolean);
    return tags.length > 0 ? tags.join(', ') : '—';
  }

  // Handle select fields - show label instead of value
  if (fieldConfig.type === 'select' && fieldConfig.options) {
    const option = fieldConfig.options.find(opt => opt.value === rawValue);
    if (option && typeof option.label === 'string') return option.label;
    return safeStringify(rawValue);
  }

  // Handle date fields - format nicely
  if (fieldConfig.type === 'date' && typeof rawValue === 'string') {
    try {
      const date = new Date(rawValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch {
      // If date parsing fails, return raw value
    }
  }

  // Default: return string representation
  return safeStringify(rawValue);
}

/**
 * Get entity detail fields based on entity type
 * Uses editConfig.ts as the single source of truth for field definitions
 * Shows all fields including primary identifiers (name/title)
 */
/**
 * Detail rows for a type that edits through its list config (contractual
 * parties): the fields its kind shows, each read through `display`, a select's
 * option label, or the raw value.
 */
/** One detail row; `field` is set where the value can be edited inline. */
interface DetailField {
  key: string;
  label: string;
  value: string;
  field?: EntityField;
}

function listConfigDetailFields(record: Record<string, unknown>): DetailField[] {
  if (!listConfig.value) return [];
  // The chip column (a party's kind) already sits beside the title.
  const chipKey = listConfig.value.columns.find(column => column.kind === 'chip')?.key;
  return allFieldsOf(listConfig.value)
    .filter(field => field.key !== chipKey && isFieldShown(field, record))
    .map(field => {
      const raw = record[field.key];
      const option = field.options?.find(candidate => candidate.value === raw);
      const text = field.display
        ? field.display(record as EntityRow)
        : toText(option?.label ?? raw);
      // Create-only fields (a party's kind) read, but don't edit, in place.
      return {
        key: field.key,
        label: field.label,
        value: text.trim() || '—',
        field: field.createOnly ? undefined : field,
      };
    });
}

const entityDetailFields = computed<DetailField[]>(() => {
  if (!entity.value) return [];

  if (editsThroughListForm.value) {
    return listConfigDetailFields(entity.value);
  }

  // Get field configurations from editConfig
  const editFields = getEntityEditFields(entityType.value);
  if (editFields.length === 0) return [];

  // Build detail fields from edit config
  const fields = editFields.map(field => {
    const rawValue = (entity.value as Record<string, unknown>)[field.key];
    const displayValue = formatFieldValue(field, rawValue);

    return {
      key: field.key,
      label: field.label,
      value: displayValue,
    };
  });

  return fields;
});

/**
 * The entity's list config — the redesign's single source for its plural,
 * breadcrumb root and which column reads as the type chip. Absent for `view`,
 * which has no list page.
 */
const listConfig = computed(
  () => entityConfigs[entityType.value as ContractManagementEntityType] ?? undefined
);
const typePlural = computed(() => listConfig.value?.plural ?? displayName.value);
/** Types whose list config owns update (contractual parties) edit their fields inline. */
const editsThroughListForm = computed(() => Boolean(listConfig.value?.api.update));
const crumbRoots = computed(() => listConfig.value?.crumbs ?? [navLabels.contractManagement]);

function formatDay(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '';
  return new Date(raw).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDayTime(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '';
  return `${formatDay(raw)}, ${new Date(raw).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** The type chip beside the title — the same column the list draws as a chip. */
const detailChip = computed(() => {
  const chipColumn = listConfig.value?.columns.find(column => column.kind === 'chip');
  if (!chipColumn || !entity.value) return '';
  // A column with its own reader (a party's kind code → label) says it the same way here.
  if (chipColumn.value) return chipColumn.value(entity.value);
  const raw = (entity.value as Record<string, unknown>)[chipColumn.key];
  return typeof raw === 'string' ? raw : '';
});

/** "Created 9 Jul 2026 · updated 11 Jul 2026 · Legal entity" */
const detailMeta = computed(() => {
  const record = entity.value as Record<string, unknown> | null;
  if (!record) return '';
  const parts = [`Created ${formatDay(record.created_at)}`];
  if (record.updated_at) parts.push(`updated ${formatDay(record.updated_at)}`);
  parts.push(displayName.value);
  return parts.join(' · ');
});

/**
 * The Details tab's rows: every configured field, then Created and Last
 * updated. An empty value reads "Not set" in muted type rather than a dash,
 * so a blank row still says something.
 */
const detailFieldRows = computed(() => {
  const rows: (DetailField & { empty: boolean })[] = entityDetailFields.value.map(field => ({
    ...field,
    value: field.value === '—' ? 'Not set' : field.value,
    empty: field.value === '—',
  }));
  const record = entity.value as Record<string, unknown> | null;
  if (record) {
    rows.push(
      {
        key: '__created',
        label: 'Created',
        value: formatDayTime(record.created_at),
        empty: false,
      },
      {
        key: '__updated',
        label: 'Last updated',
        value: record.updated_at ? formatDayTime(record.updated_at) : 'Never edited since creation',
        empty: !record.updated_at,
      }
    );
  }
  return rows;
});

const fileCountLabel = computed(
  () => `${totalCount.value} ${totalCount.value === 1 ? 'contract file' : 'contract files'}`
);

// --- file card helpers -----------------------------------------------------
function fileName(file: ContractFileWithRelations): string {
  // The contract's own name is the last resort — it is always present.
  return (
    file.revision?.file_name ?? file.vault_file?.current_revision?.file_name ?? file.contract.name
  );
}

/** Right-hand meta on the card — the contract this file belongs to. */
function fileMetaLabel(file: ContractFileWithRelations): string {
  return file.contract.name;
}

function fileIsCurrent(file: ContractFileWithRelations): boolean {
  return file.is_latest_revision !== false;
}

function fileRevLabel(file: ContractFileWithRelations): string {
  return file.revision ? `v${file.revision.version_number}` : '';
}

function filePages(file: ContractFileWithRelations): number[] {
  return file.pages.map(page => page.page).sort((a, b) => a - b);
}

function fileMentionLabel(file: ContractFileWithRelations): string {
  const count = file.pages.length;
  return count === 1 ? '1 mention' : `${count} mentions`;
}

/** This page pill is the one currently open in the preview pane. */
function isActivePage(file: ContractFileWithRelations, page: number): boolean {
  return (
    showDocumentPreview.value &&
    selectedContractFile.value?.contract_file.uid === file.contract_file.uid &&
    selectedPage.value === page
  );
}

// --- the title's ⋯ menu ----------------------------------------------------
// No "Ask AI about this record": the chat resolves documents, not entities, so
// the action could only ever stand in for the record by handing over the files
// it appears in — which the per-file "Ask AI" below already does, precisely.
const detailMenuOptions = computed(() => {
  const options: {
    key: string;
    label: string;
    disabled?: boolean;
    props?: Record<string, unknown>;
  }[] = [];
  if (supportsDelete.value) {
    options.push(
      gateOption(
        {
          key: 'delete',
          label: `Delete ${displayName.value.toLowerCase()}`,
          props: gaiaDangerOption,
        },
        partyBits.value,
        ACCESS_PERMISSION_ADMIN
      )
    );
  }
  return options;
});

async function handleDetailMenu(key: string): Promise<void> {
  if (key === 'delete') {
    await handleDelete();
  }
}

function goToList(): void {
  const listRouteName = config.value?.listRouteName;
  if (listRouteName) void router.push({ name: listRouteName });
}

/**
 * The contract card's ⋯ menu (Matter). Remove is confirmed inline on the card
 * rather than acted on straight away — the legacy list asked before removing
 * and that shouldn't be lost in the restyle.
 */
function handleContractMenu(key: string, contract: ContractManagementContractInDb): void {
  if (key === 'open') {
    void router.push(contractRoute(contract.uid));
    return;
  }
  if (key === 'ask-ai') {
    handleAskAiAboutContract(contract.uid);
    return;
  }
  confirmRemoveContractUid.value = contract.uid;
}

/**
 * A contract is a first-class chat reference (`contractUids`), same as a
 * contract file — so the contracts tab gets the same Ask AI its neighbours
 * have. This is the entity-vs-document line: the record itself can't be
 * referenced, the contracts filed under it can.
 */
function handleAskAiAboutContract(contractUid: string): void {
  const contractUids = [contractUid];
  const limitWarning = askAiLimitWarning({ contractUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  void router.push(askAiRoute({ contractUids }));
}

/** The file card's ⋯ menu. */
function handleFileMenu(key: string, file: ContractFileWithRelations): void {
  if (key === 'open') void router.push(contractFileRoute(file.contract_file.uid));
  else handleAskAiAboutContractFile(file.contract_file.uid);
}

// Structural colours for the detail chrome; the rest is design components.
const paneSurface = gaiaNeutral.surfaceSoft;
const paneBorder = gaiaNeutral.borderMuted;
const ink = gaiaNeutral.ink;
const subtleText = gaiaNeutral.textSubtle;
const cardBorder = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;

const currentHighlights = ref<string[]>([]);

// Fetch entity basic info
async function fetchEntity(): Promise<void> {
  if (!entityUid.value || !config.value) return;

  unavailable.value = false;
  entityFailed.value = false;
  try {
    entity.value = await config.value.getEntityFunction(entityUid.value);
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Entity not available:', error);
      unavailable.value = true;
      return;
    }
    logger.error('Error fetching entity:', error);
    entityFailed.value = true;
    message.error(`Failed to load ${displayName.value.toLowerCase()} details`);
  }
}

// Build search query from current state
function buildSearchQuery(): FilterableSearchQuery {
  // Ensure pageSize doesn't exceed backend maximum of 50
  const effectivePageSize = Math.min(pageSize.value, 50);

  const query: FilterableSearchQuery = {
    text: searchText.value || '',
    limit: effectivePageSize,
    offset: (currentPage.value - 1) * effectivePageSize,
    sort_key: sortKey.value,
    sort_direction: sortDirection.value,
    similarity_threshold: 0.5,
  };

  // Add column filters if any are active
  const columnFilters = buildColumnFilters();

  if (Object.keys(columnFilters).length > 0) {
    query.column_filters = columnFilters;
  }

  return query;
}

// Fetch contract files or contracts (depending on entity type)
async function fetchContractFiles(): Promise<void> {
  if (!entityUid.value || !config.value) return;

  loading.value = true;
  try {
    const searchQuery = buildSearchQuery();
    const response = await config.value.getDetailFunction(
      entityUid.value,
      searchQuery,
      showsContracts.value ? undefined : latestRevisionOnly.value
    );

    if (showsContracts.value) {
      // A matter or party: ContractManagementContractSearchResult
      const contractResponse = response as ContractManagementContractSearchResult;
      contracts.value = contractResponse.results;
      totalCount.value = contractResponse.result_count;

      // Update filter options from response
      const convertedOptions: Record<string, (string | null)[]> = {};
      for (const [key, values] of Object.entries(contractResponse.filter_options)) {
        convertedOptions[key] = values.map(v => (v === null ? null : safeStringify(v)));
      }
      filterOptions.value = convertedOptions;
    } else {
      // For other entities, response is RelatedContractFilesResponse
      const fileResponse = response as RelatedContractFilesResponse;
      contractFiles.value = fileResponse.contract_files.results;
      totalCount.value = fileResponse.contract_files.result_count;

      // Update filter options from response
      const convertedOptions: Record<string, (string | null)[]> = {};
      for (const [key, values] of Object.entries(fileResponse.contract_files.filter_options)) {
        convertedOptions[key] = values.map(v => (v === null ? null : safeStringify(v)));
      }
      filterOptions.value = convertedOptions;
    }
  } catch (error) {
    logger.error('Error fetching data:', error);
    message.error(
      showsContracts.value ? 'Failed to load contracts' : 'Failed to load contract files'
    );
  } finally {
    loading.value = false;
  }
}

// The search field owns the debounce and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1; // Reset to first page on search
  await fetchContractFiles();
}

// Watch for page changes
watch(currentPage, () => {
  if (currentPage.value > 0) {
    void fetchContractFiles();
  }
});

// Watch for page size changes
watch(pageSize, () => {
  if (pageSize.value > 0) {
    void fetchContractFiles();
  }
});

// Handle page change
function handlePageChange(page: number): void {
  currentPage.value = page;
}

// Handle page size change
function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1; // Reset to first page when changing page size
}

// Build column filters for API
function buildColumnFilters(): { [key: string]: string[] } {
  const filters: { [key: string]: string[] } = {};

  for (const [key, value] of Object.entries(activeFilters.value)) {
    if (Array.isArray(value) && value.length > 0) {
      // Convert filter values, handling null values properly
      // API expects Array<string>, so convert null to empty string
      const convertedValues = value.map(v => {
        // Handle special display values - convert "(Empty)" to empty string
        if (v === '(Empty)') {
          return '';
        }
        return String(v);
      });

      if (convertedValues.length > 0) {
        filters[key] = convertedValues;
      }
    }
  }

  return filters;
}

// --- State filters (contracts tab) -----------------------------------------
// A contract list filters by its contracts' and amendments' states. The
// backend offers both keys in `filter_options`, in severity order.
const STATE_FILTERS = [
  { key: 'contract_state', label: 'Contract state' },
  { key: 'amendment_state', label: 'Amendment state' },
] as const;

const stateFilters = computed(() =>
  showsContracts.value
    ? STATE_FILTERS.filter(
        filter => filter.key in filterOptions.value && filterOptions.value[filter.key].length > 0
      )
    : []
);
const openStateChip = ref<string | null>(null);
const stateSearch = ref<Record<string, string>>({});

function stateFilterLabel(value: string): string {
  return value in contractIconStates ? contractStateLabel(value as ContractIconState) : value;
}

function pickedStates(key: string): string[] {
  const values = activeFilters.value[key];
  return Array.isArray(values) ? values.map(value => String(value)) : [];
}

function stateFilterValues(key: string): GaiaFilterValue[] {
  const picked = pickedStates(key);
  const term = (stateSearch.value[key] ?? '').trim().toLowerCase();
  return (filterOptions.value[key] ?? [])
    .filter((value): value is string => Boolean(value))
    .map(value => ({ key: value, label: stateFilterLabel(value), checked: picked.includes(value) }))
    .filter(option => !term || option.label.toLowerCase().includes(term));
}

function setStateFilter(key: string, values: string[]): void {
  const next = { ...activeFilters.value };
  if (values.length > 0) next[key] = values;
  else delete next[key];
  activeFilters.value = next;
  currentPage.value = 1;
  void fetchContractFiles();
}

function toggleStateFilter(key: string, value: string): void {
  const picked = pickedStates(key);
  setStateFilter(
    key,
    picked.includes(value) ? picked.filter(state => state !== value) : [...picked, value]
  );
}

function removeStateFilterLabel(key: string, label: string): void {
  setStateFilter(
    key,
    pickedStates(key).filter(value => stateFilterLabel(value) !== label)
  );
}

function clearStateFilters(): void {
  activeFilters.value = {};
  currentPage.value = 1;
  void fetchContractFiles();
}

// Handle latest revision toggle
function handleLatestRevisionToggle(): void {
  currentPage.value = 1;
  void fetchContractFiles();
}

// Handle edit
function handleEdit(): void {
  if (!entity.value) return;
  showEditDialog.value = true;
}

// Handle update
async function handleUpdate(data: Record<string, unknown>): Promise<void> {
  if (!config.value?.updateFunction || !entityUid.value) return;

  saving.value = true;
  try {
    // Use the response from updateFunction directly instead of making an extra GET request
    const updatedEntity = await config.value.updateFunction(entityUid.value, data);
    entity.value = updatedEntity;
    message.success(`${displayName.value} updated successfully`);
    showEditDialog.value = false;
  } catch (error) {
    logger.error('Error updating entity:', error);
    message.error(`Failed to update ${displayName.value.toLowerCase()}`);
  } finally {
    saving.value = false;
  }
}

// Handle delete
async function handleDelete(): Promise<void> {
  if (!config.value?.deleteFunction || !entityUid.value) return;

  deleting.value = true;
  try {
    await config.value.deleteFunction(entityUid.value);
    message.success(`${displayName.value} deleted successfully`);
    // Navigate back to list view
    await router.push({ name: config.value.listRouteName });
  } catch (error) {
    logger.error('Error deleting entity:', error);
    message.error(`Failed to delete ${displayName.value.toLowerCase()}`);
  } finally {
    deleting.value = false;
  }
}

// Check if entity supports update/delete
const supportsUpdate = computed(() => {
  return config.value?.updateFunction !== undefined || editsThroughListForm.value;
});

const supportsDelete = computed(() => {
  return config.value?.deleteFunction !== undefined;
});

// Handle file click (for page number clicks - opens document preview)
function handleFileClick(contractFile: ContractFileWithRelations, page?: number): void {
  selectedContractFile.value = contractFile;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
  selectedPage.value = page || null;
  showDocumentPreview.value = true;

  // If a specific page is provided, navigate to it
  if (page && pdfPreviewRef.value) {
    void nextTick(() => {
      showInPdf(page, contractFile);
    });
  } else if (contractFile.pages.length > 0) {
    // Navigate to first page if no specific page provided
    const firstPage = contractFile.pages[0].page;
    selectedPage.value = firstPage;
    void nextTick(() => {
      showInPdf(firstPage, contractFile);
    });
  }

  // Force PDF reload when switching files
  pdfKey.value++;
}

// Check if entity type supports add/remove contracts (matter)
const supportsAddRemoveContracts = computed(() => {
  return entityType.value === 'matter';
});

function handleAskAiAboutContractFile(contractFileUid: string): void {
  const contractFileUids = [contractFileUid];
  const limitWarning = askAiLimitWarning({ contractFileUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  void router.push(askAiRoute({ contractFileUids }));
}

// Handle assign contract file
function handleAssignContractFile(): void {
  showAssignModal.value = true;
}

// Handle contract file assigned
async function handleContractFileAssigned(): Promise<void> {
  await fetchContractFiles();
}

// Handle remove contract from matter
async function handleRemoveContract(contractUid: string): Promise<void> {
  if (!entityUid.value || !supportsAddRemoveContracts.value) {
    message.error('Remove operation is not supported for this entity type');
    return;
  }

  // Hand the card's state slot over from the confirm strip to the busy one.
  confirmRemoveContractUid.value = null;
  removingContractUids.value.add(contractUid);

  try {
    await ContractsService.removeMatterFromContract(entityUid.value, contractUid);
    message.success('Contract removed from matter successfully');
    // Refresh contracts list
    await fetchContractFiles();
  } catch (error) {
    logger.error('Error removing contract:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to remove contract. Please try again.';
    message.error(errorMsg);
  } finally {
    removingContractUids.value.delete(contractUid);
  }
}

// Handle assign contract
function handleAssignContract(): void {
  showAssignContractModal.value = true;
}

// Handle contract assigned
async function handleContractAssigned(contractUid: string): Promise<void> {
  if (!entityUid.value || !supportsAddRemoveContracts.value) {
    message.error('Assign operation is not supported for this entity type');
    return;
  }

  try {
    await ContractsService.addMatterToContract(entityUid.value, contractUid);
    message.success('Contract assigned to matter successfully');
    // Refresh contracts list
    await fetchContractFiles();
  } catch (error) {
    logger.error('Error assigning contract:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to assign contract. Please try again.';
    message.error(errorMsg);
  }
}

// Handle close document preview
function handleClosePreview(): void {
  showDocumentPreview.value = false;
  selectedContractFile.value = null;
  selectedPage.value = null;
  currentHighlights.value = [];

  // Clear highlights in PDF component
  if (pdfPreviewRef.value) {
    pdfPreviewRef.value.clearHighlights();
  }
}

// Get highlight text for the entity - use only the primary identifier
// This prevents fuzzy matching from finding incorrect partial matches
//
// IMPORTANT: The fuzzy matching in GaiaVaultFilePdfPreview can be too aggressive.
// It tries to find partial matches when exact matches fail, which can lead to
// false positives (e.g., searching for "berlin" might highlight "rmance" from "performance").
//
// To minimize this, we:
// 1. Only use the primary identifier (name/title), not full text
// 2. Clean and normalize the text
// 3. The fuzzy matching will still try partial matches, but using only the entity name
//    reduces the chance of false matches
const getHighlightText = computed(() => {
  if (!entity.value) return [];

  const highlightTexts: string[] = [];

  const entityRecord = entity.value as Record<string, unknown>;

  // Prioritize name (obligations and parties carry one)
  // Clean the name to remove extra whitespace and normalize
  if (typeof entityRecord.name === 'string' && entityRecord.name) {
    const cleanName = entityRecord.name.trim();
    // Only add if it's a reasonable length (not too short to avoid false matches)
    // and not too long (to avoid matching too much)
    if (cleanName.length >= 3 && cleanName.length <= 100) {
      highlightTexts.push(cleanName);
    }
  }
  // Use title for clauses
  else if (typeof entityRecord.title === 'string' && entityRecord.title) {
    const cleanTitle = entityRecord.title.trim();
    if (cleanTitle.length >= 3 && cleanTitle.length <= 100) {
      highlightTexts.push(cleanTitle);
    }
  }

  // Don't use text field for highlighting as it's too long and causes false matches
  // The entity name/title should be sufficient to find mentions in the document

  return highlightTexts;
});

// Check if a page is in the list of pages where entity appears
function isPageInEntityPages(
  page: number,
  contractFile: ContractFileWithRelations | null
): boolean {
  if (!contractFile) return false;
  return contractFile.pages.some(p => p.page === page);
}

// Show page in PDF with highlighting
function showInPdf(page: number, contractFile?: ContractFileWithRelations | null): void {
  if (pdfPreviewRef.value) {
    selectedPage.value = page;
    pdfPreviewRef.value.goToPage(page);

    // Only highlight if this page is in the list of pages where entity appears
    const fileToCheck = contractFile ?? selectedContractFile.value;
    const shouldHighlight = isPageInEntityPages(page, fileToCheck);

    if (shouldHighlight) {
      // Use entity name/title for highlighting - only the primary identifier
      // This prevents fuzzy matching from finding incorrect partial matches
      const highlightText = getHighlightText.value;

      // Filter out empty strings and ensure we have valid text to highlight
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        currentHighlights.value = validHighlightText;

        // Set highlight text after a small delay to ensure page is loaded
        // Use a longer delay to ensure PDF text layer is fully rendered
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
          }
        }, 500);
      } else {
        // No valid text to highlight
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't contain entity
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

// Handle document load
function handleDocumentLoad(): void {
  // If we have a selected page, navigate to it and apply highlights
  if (selectedPage.value && pdfPreviewRef.value) {
    void nextTick(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      const page = selectedPage.value || 1;
      pdfPreviewRef.value?.goToPage(page);
      // Apply highlights after page navigation, only if page contains entity
      setTimeout(() => {
        if (pdfPreviewRef.value && selectedPage.value) {
          const shouldHighlight = isPageInEntityPages(
            selectedPage.value,
            selectedContractFile.value
          );
          if (shouldHighlight) {
            const highlightText = getHighlightText.value;
            const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);
            if (validHighlightText.length > 0) {
              pdfPreviewRef.value.setHighlightText(validHighlightText);
            } else {
              pdfPreviewRef.value.setHighlightText([]);
            }
          } else {
            pdfPreviewRef.value.setHighlightText([]);
          }
        }
      }, 500); // Delay to ensure page and text layer are fully rendered
    });
  }
}

// Handle highlights cleared
function handleHighlightsCleared(): void {
  currentHighlights.value = [];
}

// Handle page change from PDF component
function handlePdfPageChange(page: number): void {
  // Update selected page
  selectedPage.value = page;

  // Check if this page contains the entity and update highlights accordingly
  if (pdfPreviewRef.value && selectedContractFile.value) {
    const shouldHighlight = isPageInEntityPages(page, selectedContractFile.value);

    if (shouldHighlight) {
      const highlightText = getHighlightText.value;
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        // Apply highlights for this page with a delay to ensure text layer is ready
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
            currentHighlights.value = validHighlightText;
          }
        }, 300);
      } else {
        // Clear highlights if no valid text
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't contain entity
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

/**
 * The record first: its files and shares hang off it, so a refused record
 * means one "not available" state rather than a toast per request.
 */
async function loadEntity(): Promise<void> {
  await fetchEntity();
  if (unavailable.value) return;
  await Promise.all([fetchContractFiles(), fetchShareCount()]);
}

// Lifecycle
onMounted(async () => {
  if (!config.value) {
    message.error('Invalid entity type');
    // A malformed URL has no parent to climb to; replace it so it leaves no
    // history entry behind.
    void router.replace({ name: 'home' });
    return;
  }
  await loadEntity();
});

// Watch for route changes
watch(
  () => [route.params.entityType, route.params.uid],
  // Compared with the previous params: `entityType` / `entityUid` read the same
  // route, so they already hold the new values and would never differ.
  async ([newEntityType, newUid], [oldEntityType, oldUid]) => {
    if (newEntityType && newUid && (newEntityType !== oldEntityType || newUid !== oldUid)) {
      // Reset search, filters, and pagination
      searchText.value = '';
      currentPage.value = 1;
      pageSize.value = 10; // Reset to default page size
      activeFilters.value = {};
      sortKey.value = 'created_at';
      sortDirection.value = 'desc';

      await loadEntity();
      selectedContractFile.value = null;
      selectedPage.value = null;
      currentHighlights.value = [];
      showDocumentPreview.value = false;
    }
  }
);

// Watch for page changes in PDF to update highlights
// Note: This will be handled by the PDF component's pageChange event if available
// For now, we handle it in showInPdf function
</script>

<template>
  <!-- Entity detail, per the Entity List Template mock: breadcrumb, serif name
       with its type chip, Details / Contract files tabs, and the linked-file
       list beside the document preview. -->
  <Layout :content-max-width="gaiaContentWidth.record">
    <template #breadcrumb>
      <n-breadcrumb-item v-for="crumb in crumbRoots" :key="crumb">{{ crumb }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link v-if="config?.listRouteName" :to="{ name: config.listRouteName }">
          {{ typePlural }}
        </router-link>
        <template v-else>{{ typePlural }}</template>
      </n-breadcrumb-item>
      <n-breadcrumb-item v-if="!unavailable">{{ entityDisplayName }}</n-breadcrumb-item>
    </template>
    <!-- The record is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable">
      <template #actions>
        <gaia-button type="primary" size="small" @click="goToList">
          Back to {{ typePlural.toLowerCase() }}
        </gaia-button>
      </template>
    </gaia-not-found>
    <n-flex v-else vertical :size="24">
      <n-flex vertical :size="14">
        <gaia-empty-state
          v-if="entityFailed"
          status="alert"
          :title="`Couldn’t load this ${displayName.toLowerCase()}`"
        >
          The record didn’t come back from the server. It may have been deleted, or the request
          failed.
          <template #actions>
            <gaia-button type="primary" size="small" @click="fetchEntity">Try again</gaia-button>
            <gaia-button size="small" @click="goToList">
              Back to {{ typePlural.toLowerCase() }}
            </gaia-button>
          </template>
        </gaia-empty-state>

        <template v-else>
          <n-flex align="flex-start" :size="[20, 14]">
            <n-flex vertical :size="6" :style="{ minWidth: 0 }">
              <!-- Centred, not baseline: a small caps chip on the display serif's
                   baseline rides visibly high. -->
              <n-flex align="center" :size="10">
                <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
                  {{ entityDisplayName || '—' }}
                </gaia-header>
                <gaia-tag v-if="detailChip" variant="label">{{ detailChip }}</gaia-tag>
              </n-flex>
              <gaia-text size="micro" tone="subtle">{{ detailMeta }}</gaia-text>
            </n-flex>

            <n-flex align="center" :size="8" :wrap="false" :style="{ marginLeft: 'auto' }">
              <gaia-button
                v-if="supportsUpdate && !editsThroughListForm"
                size="medium"
                @click="handleEdit"
                >Edit</gaia-button
              >
              <!-- Delete is the only entry left, and not every entity has one —
                 an empty ⋯ opens onto nothing, so it doesn't render. -->
              <gaia-dropdown
                v-if="detailMenuOptions.length"
                :options="detailMenuOptions"
                placement="bottom-end"
                @select="handleDetailMenu"
              >
                <gaia-icon-button :dimension="36" title="More">
                  <n-icon :component="IconMore" :size="18" />
                </gaia-icon-button>
              </gaia-dropdown>
            </n-flex>
          </n-flex>

          <!-- Tabs, with the files toolbar pushed to the right of the same row. -->
          <gaia-tab-bar
            :items="recordTabs"
            :active="activeTab"
            :style="{ marginTop: '4px' }"
            @select="(key: string) => (activeTab = key as RecordTab)"
          >
            <template v-if="activeTab !== 'detail' && activeTab !== 'sharing'" #actions>
              <gaia-search-input
                v-model:value="searchText"
                :placeholder="showsContracts ? 'Search contracts' : 'Search files'"
                :debounce="500"
                @search="applySearch"
              />
              <n-tooltip v-if="activeTab === 'contract-files' && !showsContracts" :delay="400">
                <template #trigger>
                  <n-flex align="center" :size="8" :wrap="false" class="rev-toggle">
                    <gaia-switch
                      v-model:value="latestRevisionOnly"
                      size="small"
                      @update:value="handleLatestRevisionToggle"
                    />
                    <gaia-text size="control" tone="text">Current revisions only</gaia-text>
                  </n-flex>
                </template>
                Contract files linked only to older versions of a file are excluded. The latest
                version and newly uploaded files are always included.
              </n-tooltip>
              <gaia-button
                v-if="isParty && activeTab === 'contracts'"
                type="primary"
                size="medium"
                :disabled="writeBlock !== null"
                :title="writeBlock ?? undefined"
                @click="handleAssignContractFile"
              >
                Assign contract file
              </gaia-button>
              <gaia-button
                v-if="supportsAddRemoveContracts && activeTab === 'contracts'"
                type="primary"
                size="medium"
                @click="handleAssignContract"
              >
                Assign contract
              </gaia-button>
            </template>
          </gaia-tab-bar>
        </template>
      </n-flex>

      <!-- Details tab: the record's fields in two columns. -->
      <div v-if="activeTab === 'detail'">
        <!-- The record is still on its way: label + value bars where the field
           rows will land, instead of a blank body. -->
        <gaia-table-skeleton
          v-if="loading"
          :row-style="{ gridTemplateColumns: '150px minmax(0, 1fr)' }"
          :cells="[{ width: '88px' }, '56%']"
          :rows="6"
          :row-height="46"
          :action="false"
        />
        <div v-else-if="entity && detailFieldRows.length" class="field-grid">
          <div v-for="field in detailFieldRows" :key="field.key" class="field-row">
            <gaia-text size="control" weight="semibold" tone="tertiary">{{
              field.label
            }}</gaia-text>
            <!-- List-config types edit each value where it stands. -->
            <entity-inline-field
              v-if="field.field && listConfig && entity"
              :config="listConfig"
              :row="entity"
              :field="field.field"
              :text="field.value"
              :empty="field.empty"
              :blocked-reason="writeBlock"
              @saved="fetchEntity"
            />
            <gaia-text
              v-else
              size="body"
              :tone="field.empty ? 'muted' : 'ink'"
              :style="{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }"
            >
              {{ field.value }}
            </gaia-text>
          </div>
          <n-flex v-if="!showsContracts" align="center" :size="10" class="field-foot" :wrap="false">
            <gaia-text size="control" tone="subtle">
              Referenced by {{ fileCountLabel }} —
            </gaia-text>
            <gaia-link :underline="false" @click="activeTab = 'contract-files'">
              view contract files
            </gaia-link>
          </n-flex>
        </div>
        <gaia-empty-state
          v-else-if="entity"
          :title="`No additional details for this ${displayName.toLowerCase()}`"
        >
          Nothing has been extracted or entered beyond the record’s name.
        </gaia-empty-state>
      </div>

      <!-- Contract files tab: the linked-file list, beside the preview pane. -->
      <div v-else-if="activeTab === 'contract-files'" class="files-body">
        <div class="file-list" :class="{ narrow: showDocumentPreview }">
          <gaia-table-skeleton
            v-if="loading"
            :row-style="{ gridTemplateColumns: 'minmax(0, 1fr) 120px' }"
            :cells="['46%', '96px']"
            :rows="3"
            :row-height="64"
            :action="false"
          />
          <gaia-empty-state
            v-else-if="contractFiles.length === 0"
            :title="`No contract files reference this ${displayName.toLowerCase()} yet`"
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 2.5h5l3 3v8H4z" />
              </svg>
            </template>
            Links appear automatically when a document mentioning it is analysed. You can also
            assign a file by hand.
          </gaia-empty-state>

          <n-flex v-else vertical :size="12">
            <gaia-file-link-card
              v-for="file in contractFiles"
              :key="file.contract_file.uid"
              :name="fileName(file)"
              :current="fileIsCurrent(file)"
              :rev-label="fileRevLabel(file)"
              :meta-label="fileMetaLabel(file)"
              class="file-card"
              :to="contractFileRoute(file.contract_file.uid)"
            >
              <template #trailing>
                <gaia-dropdown
                  :options="[
                    { key: 'open', label: 'Open contract file' },
                    { key: 'ask-ai', label: 'Ask AI about this file' },
                  ]"
                  placement="bottom-end"
                  @select="(key: string) => handleFileMenu(key, file)"
                >
                  <gaia-icon-button :dimension="26" quaternary title="More">
                    <n-icon :component="IconMore" :size="16" />
                  </gaia-icon-button>
                </gaia-dropdown>
              </template>

              <template v-if="filePages(file).length">
                <gaia-text variant="label" size="nano" tone="muted">Mentions</gaia-text>
                <button
                  v-for="page in filePages(file)"
                  :key="page"
                  type="button"
                  class="page-pill"
                  :class="{ active: isActivePage(file, page) }"
                  :title="`Open the document at page ${page} and highlight the match`"
                  @click.stop="handleFileClick(file, page)"
                >
                  p. {{ page }}
                </button>
                <gaia-text size="meta" tone="faint">{{ fileMentionLabel(file) }}</gaia-text>
              </template>
            </gaia-file-link-card>

            <gaia-pagination
              v-if="totalCount > pageSize"
              :page="currentPage"
              :page-size="pageSize"
              :total="totalCount"
              @update:page="handlePageChange"
              @update:page-size="handlePageSizeChange"
            />
          </n-flex>
        </div>

        <!-- Document preview: the app's real PDF viewer in the mock's pane. -->
        <div v-if="showDocumentPreview" class="preview-pane">
          <n-flex align="center" :size="10" :wrap="false" class="pane-head">
            <gaia-text variant="label" size="nano" tone="faint" :style="{ flex: 'none' }">
              Document preview
            </gaia-text>
            <gaia-text truncate size="control" weight="semibold" tone="ink">
              {{ selectedVaultFileName }}
            </gaia-text>
            <gaia-icon-button
              :dimension="26"
              quaternary
              title="Close preview"
              :style="{ marginLeft: 'auto' }"
              @click="handleClosePreview"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </gaia-icon-button>
          </n-flex>
          <div class="pane-body">
            <gaia-vault-file-pdf-preview
              v-if="selectedVaultFileUid"
              ref="pdfPreviewRef"
              :key="pdfKey"
              :vault-file-uid="selectedVaultFileUid"
              :revision-uid="selectedRevisionUid"
              :revision-preview-status="selectedRevisionPreviewStatus"
              @loaded="handleDocumentLoad"
              @highlights-cleared="handleHighlightsCleared"
              @page-change="handlePdfPageChange"
            />
          </div>
        </div>
      </div>

      <!-- Sharing tab (contractual parties only). -->
      <contractual-party-shares
        v-else-if="activeTab === 'sharing'"
        :party-uid="entityUid"
        :party-name="entityDisplayName"
        :blocked-reason="adminBlock"
        @count="(count: number) => (shareCount = count)"
      />

      <!-- Contracts tab (Matter only). The mock draws no contracts tab, so it
         borrows the files tab's vocabulary verbatim — same card, same trailing
         ⋯, same inline state strips — rather than keeping its own look. -->
      <div v-else>
        <!-- Both state filters sit on the strip at once: there are only two. -->
        <gaia-filter-bar
          v-if="stateFilters.length > 0"
          :can-add="false"
          :show-reset="Object.keys(buildColumnFilters()).length > 0"
          :style="{ marginBottom: '12px' }"
          @reset="clearStateFilters"
        >
          <gaia-popover
            v-for="filter in stateFilters"
            :key="filter.key"
            placement="bottom-start"
            :show="openStateChip === filter.key"
            @update:show="(show: boolean) => (openStateChip = show ? filter.key : null)"
          >
            <template #trigger>
              <gaia-filter-chip
                :field="filter.label"
                :values="pickedStates(filter.key).map(stateFilterLabel)"
                :active="openStateChip === filter.key"
                @toggle="openStateChip = openStateChip === filter.key ? null : filter.key"
                @remove="setStateFilter(filter.key, [])"
                @remove-value="(label: string) => removeStateFilterLabel(filter.key, label)"
              />
            </template>
            <gaia-filter-value-panel
              :values="stateFilterValues(filter.key)"
              :search="stateSearch[filter.key] ?? ''"
              @update:search="(term: string) => (stateSearch[filter.key] = term)"
              @toggle="(value: string) => toggleStateFilter(filter.key, value)"
              @clear="setStateFilter(filter.key, [])"
            />
          </gaia-popover>
        </gaia-filter-bar>

        <div class="file-list">
          <gaia-table-skeleton
            v-if="loading"
            :row-style="{ gridTemplateColumns: 'minmax(0, 1fr) 120px' }"
            :cells="['46%', '96px']"
            :rows="3"
            :row-height="64"
            :action="false"
          />
          <gaia-empty-state
            v-else-if="contracts.length === 0"
            :title="
              isParty
                ? 'No contracts with this party yet'
                : `No contracts filed under this ${displayName.toLowerCase()} yet`
            "
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3.5 2.5h6l3 3v8h-9zM9 2.5v3h3M6 9h4" />
              </svg>
            </template>
            <template v-if="isParty">
              A contract shows here once one of its files is assigned to this party.
            </template>
            <template v-else>
              Assign a contract to start filing work against this
              {{ displayName.toLowerCase() }}.
            </template>
            <template v-if="supportsAddRemoveContracts || isParty" #actions>
              <gaia-button
                type="primary"
                size="medium"
                :disabled="writeBlock !== null"
                :title="writeBlock ?? undefined"
                @click="isParty ? handleAssignContractFile() : handleAssignContract()"
              >
                {{ isParty ? 'Assign contract file' : 'Assign contract' }}
              </gaia-button>
            </template>
          </gaia-empty-state>

          <n-flex v-else vertical :size="12">
            <gaia-file-link-card
              v-for="contract in contracts"
              :key="contract.uid"
              :name="contract.name || 'Untitled contract'"
              :meta-label="formatDay(contract.created_at)"
              class="file-card"
              :to="contractRoute(contract.uid)"
            >
              <template #trailing>
                <gaia-dropdown
                  :options="[
                    { key: 'open', label: 'Open contract' },
                    { key: 'ask-ai', label: 'Ask AI about this contract' },
                    ...(supportsAddRemoveContracts
                      ? [
                          {
                            key: 'remove',
                            label: `Remove from this ${displayName.toLowerCase()}`,
                            props: gaiaDangerOption,
                          },
                        ]
                      : []),
                  ]"
                  placement="bottom-end"
                  @select="(key: string) => handleContractMenu(key, contract)"
                >
                  <gaia-icon-button :dimension="26" quaternary title="More">
                    <n-icon :component="IconMore" :size="16" />
                  </gaia-icon-button>
                </gaia-dropdown>
              </template>

              <gaia-contract-state-cell :states="contract.states" />
              <gaia-tag v-if="contract.contract_type" variant="label">{{
                contract.contract_type.name
              }}</gaia-tag>
              <gaia-tag v-for="tag in contract.tags" :key="tag" variant="muted">{{ tag }}</gaia-tag>
              <gaia-text v-if="contract.description" truncate size="meta" tone="faint">
                {{ contract.description }}
              </gaia-text>

              <template v-if="confirmRemoveContractUid === contract.uid" #state>
                <gaia-table-row-state
                  variant="confirm"
                  :title="`Remove “${contract.name || 'Untitled contract'}”?`"
                  confirm-label="Remove"
                  @cancel="confirmRemoveContractUid = null"
                  @confirm="handleRemoveContract(contract.uid)"
                >
                  The contract stays in contract management — only its link to this
                  {{ displayName.toLowerCase() }} is removed.
                </gaia-table-row-state>
              </template>
              <template v-else-if="removingContractUids.has(contract.uid)" #state>
                <gaia-table-row-state variant="busy" note="Undo not available">
                  Removing “{{ contract.name || 'Untitled contract' }}”…
                </gaia-table-row-state>
              </template>
            </gaia-file-link-card>

            <gaia-pagination
              v-if="totalCount > pageSize"
              :page="currentPage"
              :page-size="pageSize"
              :total="totalCount"
              @update:page="handlePageChange"
              @update:page-size="handlePageSizeChange"
            />
          </n-flex>
        </div>
      </div>
    </n-flex>

    <ContractManagementEditDialog
      v-if="supportsUpdate && !editsThroughListForm"
      v-model:show="showEditDialog"
      :entity-type="entityType"
      :initial-data="entity || {}"
      :loading="saving"
      @submit="handleUpdate"
    />

    <AssignContractFileModal
      v-if="isParty && entityUid"
      v-model:show="showAssignModal"
      :entity-uid="entityUid"
      @assigned="handleContractFileAssigned"
    />

    <AssignContractModal
      v-if="supportsAddRemoveContracts && entityUid"
      v-model:show="showAssignContractModal"
      :matter-uid="entityUid"
      @assigned="handleContractAssigned"
    />
  </Layout>
</template>

<style scoped>
.rev-toggle {
  height: 36px;
  padding: 0 11px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 9px;
  white-space: nowrap;
}

/* Two columns of field rows, each row a label above/beside its value. */
.field-grid {
  width: 100%;
  display: grid;
  /* Two columns, as the mock draws them — collapsing to one when there isn't
     room for both. */
  grid-template-columns: 1fr;
  column-gap: 56px;
}

.field-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  align-items: start;
  padding: 14px 0;
  border-bottom: 1px solid v-bind(paneBorder);
}

.field-foot {
  grid-column: 1 / -1;
  padding-top: 18px;
}

@media (min-width: 1100px) {
  .field-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Files tab: list beside the preview pane. */
/* The list scrolls beside a preview that fills the pane, so the pair needs a
   definite height now that the page itself scrolls — the same viewport budget
   ContractFileDetailView gives its split. */
.files-body {
  display: flex;
  gap: 0;
  padding-right: 0;
  height: calc(100vh - 256px);
  overflow: hidden;
}

.file-list {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding-right: 32px;
}

.file-list.narrow {
  flex: 1 1 45%;
}

.file-card {
  padding: 13px 14px 14px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
}

/* A page pill jumps the preview to that page and highlights the match. */
.page-pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 6px;
  background: v-bind(surface);
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: v-bind(subtleText);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.page-pill:hover {
  border-color: v-bind(ink);
  color: v-bind(ink);
}

.page-pill.active {
  border-color: v-bind(ink);
  background: v-bind(ink);
  color: v-bind(surface);
}

.preview-pane {
  flex: 1.15;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid v-bind(paneBorder);
  background: v-bind(paneSurface);
  overflow: hidden;
}

.pane-head {
  flex: none;
  padding: 12px 18px;
  border-bottom: 1px solid v-bind(paneBorder);
  background: v-bind(surface);
}

.pane-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
